import { Container, Row, Col, Card, Button, Spinner } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { User } from "../components/dashboard/interfaces/User";
import { Recording } from "../components/dashboard/interfaces/Recording";
import RoleBadge from "../components/dashboard/RoleBadge";
import RecordingCard from "../components/dashboard/RecordingCard";
import { requestWithAuthorization } from "../network/apiRequests";
import { MdContentCopy, MdPerson, MdArrowBack } from "react-icons/md";
import { BsCheckSquare, BsDownload, BsXLg } from "react-icons/bs";
import BulkDownloadModal, { BulkSelectedSession } from "../components/sessions/BulkDownloadModal";

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<User | null>(null);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Selection mode states
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  
  // Fetch user details and recordings
  useEffect(() => {
    if (!userId) return;

    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        // Get user data from navigation state
        const stateUser = location.state?.user as User;

        if (stateUser) {
          // Use user data from navigation state
          setUser(stateUser);
        } else {
          // Fallback: Fetch user license information (includes role)
          const userResponse = await requestWithAuthorization(
            `${process.env.REACT_APP_API_HOSTNAME}/admin/users/${userId}/license`
          );

          if (userResponse.status === 200) {
            const userData = userResponse.data;
            // Transform API response to match User interface
            const transformedUser: User = {
              uid: userData.user_id || userId,
              email: userData.email,
              display_name: userData.name,
              role: userData.role,
              plan_id: userData.plan_id,
              profile_url: userData.photo_url
            };
            setUser(transformedUser);
          } else {
            console.error('Failed to fetch user data');
          }
        }

        // Fetch user recordings
        const recordingsResponse = await requestWithAuthorization(
          `${process.env.REACT_APP_API_HOSTNAME}/${userId}/recordings`
        );

        if (recordingsResponse.status === 200) {
          const recordingsData = recordingsResponse.data.recordings || recordingsResponse.data || [];
          setRecordings(recordingsData);
        } else {
          console.error('Failed to fetch recordings');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [userId, location.state]);

  const handleRoleChange = async (newRole: 'user' | 'pro' | 'admin') => {
    if (!userId) return;

    try {
      const response = await requestWithAuthorization(
        `${process.env.REACT_APP_API_HOSTNAME}/admin/users/${userId}/license`,
        {},
        'put',
        { role: newRole }
      );

      if (response.status === 200) {
        // Update local user state with new role
        if (user) {
          setUser({
            ...user,
            role: newRole,
            plan_id: newRole === 'admin' ? 'admin' : newRole === 'pro' ? 'pro' : 'free'
          });
        }
      } else {
        throw new Error('Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  };

  const handleCopyUserId = async () => {
    if (!user) return;

    try {
      await navigator.clipboard.writeText(user.uid);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy user ID:', error);
    }
  };

  const handleEnterSelectMode = () => {
    setIsSelectMode(true);
    setSelectedSessionIds(new Set());
  };

  const handleExitSelectMode = () => {
    setIsSelectMode(false);
    setSelectedSessionIds(new Set());
  };

  const handleToggleSelect = (id: string) => {
    setSelectedSessionIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedSessionIds.size === recordings.length) {
      setSelectedSessionIds(new Set());
    } else {
      setSelectedSessionIds(new Set(recordings.map(r => r.recordingId)));
    }
  };

  const allSelected = recordings.length > 0 && selectedSessionIds.size === recordings.length;

  const selectedBulkSessions: BulkSelectedSession[] = recordings
    .filter(r => selectedSessionIds.has(r.recordingId))
    .map(r => {
      const rec = r as any;
      return {
        id: r.recordingId,
        name: rec.recordingName || r.name || 'Session',
        start: rec.start || r.timestamp || '',
        end: rec.end || ''
      };
    });

  const getDisplayName = () => {
    if (user?.display_name) {
      return user.display_name;
    }
    if (user?.email) {
      const emailParts = user.email.split('@');
      return emailParts[0];
    }
    return 'Unknown User';
  };

  if (isLoading) {
    return (
      <Container fluid className="pt-2 background-color">
        <Row className="justify-content-center">
          <Col className="text-center">
            <Spinner animation="border" variant="light" />
          </Col>
        </Row>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container fluid className="pt-2 background-color">
        <Row>
          <Col>
            <h1 className="text-white">User not found</h1>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="pt-2 background-color">
      <Row className="mb-4">
        <Col>
          <Button variant="outline-light" onClick={() => navigate('/users')}>
            <MdArrowBack className="me-2" />
            Back to Users
          </Button>
        </Col>
      </Row>

      {/* User Information Card */}
      <Row className="mb-4">
        <Col lg={12} xl={8}>
          <Card style={{ backgroundColor: '#1a1d1f', borderColor: '#495057' }}>
            <Card.Body className="p-4">
              <Row className="align-items-center">
                <Col md={2} className="text-center">
                  <div
                    className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mx-auto"
                    style={{ width: '80px', height: '80px' }}
                  >
                    {user.profile_url ? (
                      <img
                        src={user.profile_url}
                        alt="Profile"
                        className="rounded-circle"
                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                      />
                    ) : (
                      <MdPerson color="white" size={40} />
                    )}
                  </div>
                </Col>
                <Col md={10}>
                  <Card.Title as="h2" className="text-white mb-3">
                    {getDisplayName()}
                  </Card.Title>

                  <Row className="mb-3">
                    <Col md={6}>
                      <div className="mb-2">
                        <strong className="text-muted">Email:</strong>
                        <span className="text-white ms-2">{user.email}</span>
                      </div>
                      <div className="mb-2">
                        <strong className="text-muted">User ID:</strong>
                        <div className="d-inline-flex align-items-center ms-2">
                          <code className="text-white bg-secondary px-2 py-1 rounded">
                            {user.uid}
                          </code>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={handleCopyUserId}
                            className="text-light p-0 ms-2"
                            title={copySuccess ? 'Copied!' : 'Copy User ID'}
                          >
                            <MdContentCopy color={copySuccess ? '#28a745' : '#6c757d'} />
                          </Button>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong className="text-muted">Role:</strong>
                        <div className="d-inline-flex align-items-center ms-2">
                          <RoleBadge
                            role={user.role}
                            planId={user.plan_id}
                            isEditable={true}
                            onRoleChange={handleRoleChange}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recordings Section */}
      <Row>
        <Col>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h3 className="text-white mb-0">
              Recordings ({recordings.length})
            </h3>
            {recordings.length > 0 && !isSelectMode && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleEnterSelectMode}
                className="d-flex align-items-center gap-2"
                id="user-select-sessions-btn"
              >
                <BsCheckSquare />
                Select Sessions
              </Button>
            )}
          </div>

          {/* Selection Toolbar */}
          {isSelectMode && (
            <div className="sessions-selection-toolbar d-flex align-items-center gap-3 mb-3 px-3 py-2 rounded bg-dark border border-secondary">
              <input
                type="checkbox"
                id="select-all-user-sessions-checkbox"
                checked={allSelected}
                onChange={handleSelectAll}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
                aria-label="Select all sessions for this user"
              />
              <label htmlFor="select-all-user-sessions-checkbox" className="mb-0 small text-white" style={{ cursor: 'pointer' }}>
                {allSelected ? 'Deselect All' : 'Select All'}
              </label>
              <span className="text-muted small ms-2">
                {selectedSessionIds.size} selected
              </span>
              <div className="ms-auto d-flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={selectedSessionIds.size === 0}
                  onClick={() => setShowBulkModal(true)}
                  className="d-flex align-items-center gap-2"
                  id="user-download-selected-btn"
                >
                  <BsDownload />
                  Download Selected
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleExitSelectMode}
                  className="d-flex align-items-center gap-1"
                  id="user-cancel-select-btn"
                >
                  <BsXLg />
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {recordings.length === 0 ? (
            <Card style={{ backgroundColor: '#1a1d1f', borderColor: '#495057' }}>
              <Card.Body className="text-center py-5">
                <p className="text-muted">No recordings found for this user.</p>
              </Card.Body>
            </Card>
          ) : (
            <Row xs={1} md={2} lg={3} xl={4} className="g-4">
              {recordings.map((recording) => (
                <Col key={recording.recordingId}>
                  <RecordingCard
                    recording={recording}
                    userId={userId}
                    isSelectable={isSelectMode}
                    isSelected={selectedSessionIds.has(recording.recordingId)}
                    onToggleSelect={handleToggleSelect}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Col>
      </Row>

      <BulkDownloadModal
        show={showBulkModal}
        onHide={() => setShowBulkModal(false)}
        sessions={selectedBulkSessions}
        useApiKey={true}
      />
    </Container>
  );
}